/**
 * Actions menu action hooks for document state management
 *
 * These hooks provide a Zustand-style API for actions menu actions,
 * using FluxServices directly without Redux dispatch.
 */
import {DeleteProjectError} from "@buildwithflux/core";
import {useCallback} from "react";

import type {FluxServices} from "injection";
import {useFluxServices} from "injection/hooks";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

/**
 * Deletes the current project/document
 *
 * @param services - FluxServices containing documentStore, documentStorage, and pcbBakedGoodsManager
 * @returns Promise resolving to true if deletion succeeded, false otherwise
 */
export async function deleteProject(
    services: Pick<FluxServices, "documentStore" | "documentStorage" | "pcbBakedGoodsManager">,
): Promise<boolean> {
    const documentData = services.documentStore.getState().document;

    if (documentData) {
        try {
            await services.documentStorage.deleteDocument(documentData.uid);
            services.pcbBakedGoodsManager.clear();
            return true;
        } catch (error) {
            FluxLogger.captureError(new DeleteProjectError(error instanceof Error ? error : undefined));
            return false;
        }
    }

    return false;
}

/**
 * Hook to get the deleteProject action
 *
 * This action deletes the current project/document.
 *
 * @returns A function to delete the project that returns a promise resolving to success status
 */
export function useDeleteProject(): () => Promise<boolean> {
    const {documentStore, documentStorage, pcbBakedGoodsManager} = useFluxServices();

    return useCallback(() => {
        return deleteProject({documentStore, documentStorage, pcbBakedGoodsManager});
    }, [documentStore, documentStorage, pcbBakedGoodsManager]);
}

/**
 * Factory function to create actions menu actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch actions menu actions.
 *
 * @param services - FluxServices containing documentStore, documentStorage, and pcbBakedGoodsManager
 * @returns An object with actions menu action methods
 */
export function createActionsMenuActions(
    services: Pick<FluxServices, "documentStore" | "documentStorage" | "pcbBakedGoodsManager">,
) {
    return {
        deleteProject: (): Promise<boolean> => {
            return deleteProject(services);
        },
    };
}
